import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, ListGroup, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { apiClient, getPendingDocuments, reviewDocument, getProviders, checkMobileAvailability } from '../api';
import { toast } from 'react-toastify';
import { FileText, Check, X, Eye, User, Calendar, AlertTriangle, Car, ShieldAlert, CheckCircle2 } from 'lucide-react';

const getDocumentUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const baseUrl = apiClient.defaults.baseURL || '';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const cleanUrl = url.startsWith('/') ? url : '/' + url;
  return `${cleanBase}${cleanUrl}`;
};

const AdminDocumentReview = () => {
  const [pendingDocs, setPendingDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionInput, setShowRejectionInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Móvil y Proveedor Asignación
  const [providers, setProviders] = useState([]);
  const [internalCode, setInternalCode] = useState('');
  const [providerId, setProviderId] = useState('');
  const [mobileStatus, setMobileStatus] = useState({ is_available: true, conflict: null, suggestions: [] });
  const [checkingMobile, setCheckingMobile] = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Cargar documentos pendientes
      const res = await getPendingDocuments();
      setPendingDocs(res || []);
      if (res && res.length > 0) {
        setSelectedDoc(prev => {
          if (!prev) return res[0];
          const stillPending = res.find(d => d.id === prev.id);
          return stillPending || res[0];
        });
      } else {
        setSelectedDoc(null);
      }

      // 2. Cargar proveedores registrados
      const provs = await getProviders();
      if (Array.isArray(provs)) setProviders(provs);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar documentos pendientes o proveedores.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // Al cambiar el documento seleccionado
  useEffect(() => {
    if (!selectedDoc) {
      setExpirationDate('');
      setShowRejectionInput(false);
      setRejectionReason('');
      setImageError(false);
      setInternalCode('');
      setProviderId('');
      setMobileStatus({ is_available: true, conflict: null, suggestions: [] });
      return;
    }

    setShowRejectionInput(false);
    setRejectionReason('');
    setImageError(false);

    if (selectedDoc.expiration_date) {
      setExpirationDate(selectedDoc.expiration_date.split('T')[0]);
    } else if (selectedDoc.extracted_data && selectedDoc.extracted_data.expiration_date) {
      setExpirationDate(selectedDoc.extracted_data.expiration_date);
    } else {
      setExpirationDate('');
    }

    // Inicializar datos del vehículo si existen
    if (selectedDoc.vehicle) {
      setInternalCode(selectedDoc.vehicle.internal_code || '');
      setProviderId(selectedDoc.vehicle.provider_id || '');
    } else {
      setInternalCode('');
      setProviderId('');
    }
  }, [selectedDoc]);

  // Validar N° de Móvil en tiempo real
  useEffect(() => {
    if (!internalCode || !internalCode.trim()) {
      setMobileStatus({ is_available: true, conflict: null, suggestions: [] });
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingMobile(true);
      try {
        const vehicleId = selectedDoc?.vehicle?.id;
        const res = await checkMobileAvailability(internalCode, vehicleId);
        setMobileStatus(res);
      } catch (err) {
        console.error("Error al chequear móvil:", err);
      } finally {
        setCheckingMobile(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [internalCode, selectedDoc]);

  const selectDocument = (doc) => {
    setSelectedDoc(doc);
  };

  const handleReview = async (status) => {
    if (!selectedDoc) return;
    if (status === 'rejected' && !rejectionReason.trim()) {
      toast.warning("Por favor ingresa un motivo de rechazo.");
      return;
    }

    if (status === 'approved' && !mobileStatus.is_available) {
      toast.error(`⚠️ El N° de Móvil ${internalCode} ya está ocupado. Por favor elige otro o selecciona una sugerencia.`);
      return;
    }

    setSubmitting(true);
    try {
      await reviewDocument(
        selectedDoc.id, 
        status, 
        status === 'rejected' ? rejectionReason : '', 
        status === 'approved' ? expirationDate : '',
        status === 'approved' ? internalCode : '',
        status === 'approved' ? providerId : null
      );
      toast.success(status === 'approved' ? "Documento y Móvil aprobados con éxito." : "Documento rechazado con éxito.");
      await fetchPending();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-0 shadow-lg overflow-hidden" style={{ minHeight: '85vh' }}>
      <Card.Header className="bg-dark text-white p-3 border-bottom border-secondary d-flex justify-content-between align-items-center">
        <div>
          <h4 className="fw-bold tracking-tight mb-0 text-primary d-flex align-items-center gap-2">
            <FileText size={24} /> Auditoría e Inclusión de Móviles
          </h4>
          <p className="text-muted small mb-0">Revisa documentos, asigna N° de Móvil y aprueba choferes.</p>
        </div>
        <Badge bg="warning" text="dark" className="px-3 py-2 fs-6 fw-bold">
          {pendingDocs.length} Pendientes
        </Badge>
      </Card.Header>

      <Card.Body className="p-0">
        <Row className="g-0">
          {/* Columna Izquierda: Lista de Documentos Pendientes */}
          <Col md={4} className="border-end bg-light" style={{ height: '75vh', overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted small">Buscando pendientes...</p>
              </div>
            ) : pendingDocs.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <CheckCircle2 size={48} className="text-success mb-2" />
                <h6 className="fw-bold">¡Todo al día!</h6>
                <p className="small mb-0">No hay documentos ni vehículos pendientes de auditoría.</p>
              </div>
            ) : (
              <ListGroup variant="flush">
                {pendingDocs.map((doc) => (
                  <ListGroup.Item
                    key={doc.id}
                    action
                    active={selectedDoc && selectedDoc.id === doc.id}
                    onClick={() => selectDocument(doc)}
                    className="p-3 border-bottom d-flex align-items-center gap-3 transition-all"
                    style={{
                      backgroundColor: selectedDoc && selectedDoc.id === doc.id ? '#e2e8f0' : 'transparent',
                      borderLeft: selectedDoc && selectedDoc.id === doc.id ? '4px solid #3b82f6' : 'none'
                    }}
                  >
                    <div className="bg-white p-2 rounded shadow-sm flex-shrink-0 text-primary">
                      <FileText size={20} />
                    </div>
                    <div className="w-100 overflow-hidden">
                      <div className="fw-bold text-dark text-truncate mb-0.5" style={{ fontSize: '14px' }}>
                        {doc.driver_name || `Driver ID: ${doc.driver_id}`}
                      </div>
                      <div className="text-muted small text-uppercase fw-semibold tracking-wider" style={{ fontSize: '11px' }}>
                        {doc.document_type === 'licencia_frontal' ? '🪪 Licencia de Conducir (Frente)' :
                         doc.document_type === 'licencia_reverso' ? '🪪 Licencia de Conducir (Reverso)' :
                         doc.document_type === 'licencia' ? '🪪 Licencia de Conducir' : 
                         doc.document_type === 'rut_frontal' ? '🇨🇱 Cédula de Identidad (Frente)' :
                         doc.document_type === 'rut_reverso' ? '🇨🇱 Cédula de Identidad (Reverso)' :
                         doc.document_type === 'rut' ? '🇨🇱 Cédula de Identidad' : 
                         doc.document_type === 'antecedentes' ? '📄 Certificado Antecedentes' :
                         doc.document_type === 'soap' ? '🛡️ Seguro SOAP' :
                         doc.document_type === 'permiso_circulacion' ? '📋 Permiso Circulación' :
                         doc.document_type === 'revision_tecnica' ? '🔍 Revisión Técnica' :
                         doc.document_type === 'foto_vehiculo' ? '📸 Fotos Vehículo' :
                         (doc.document_type === 'decreto_mtt' || doc.document_type === 'certificado_mtt') ? '🏛️ Decreto MTT' :
                         doc.document_type === 'seguro_asientos' ? '💺 Seguro Asientos' :
                         doc.document_type === 'padron' ? '📄 Padrón del Vehículo' :
                         `📄 ${doc.document_type}`}
                      </div>
                      {doc.vehicle && (
                        <div className="text-primary small fw-bold mt-1" style={{ fontSize: '11px' }}>
                          🚘 Patente: {doc.vehicle.plate} ({doc.vehicle.vehicle_type_name})
                        </div>
                      )}
                      <div className="text-muted small mt-1 d-flex align-items-center gap-1" style={{ fontSize: '11px' }}>
                        <Calendar size={12} /> Subido: {new Date(doc.uploaded_at).toLocaleDateString('es-CL')}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Col>

          {/* Columna Derecha: Visor Lado a Lado */}
          <Col md={8} className="d-flex flex-column bg-white">
            {selectedDoc ? (
              <Row className="g-0 flex-grow-1" style={{ maxHeight: '75vh' }}>
                {/* Lado Izquierdo: Imagen / PDF Viewer */}
                <Col lg={6} className="bg-dark d-flex flex-column align-items-center justify-content-center p-3 border-end position-relative" style={{ height: '75vh', overflow: 'hidden' }}>
                  <div className="position-absolute top-2 left-2 z-3">
                    <span className="badge bg-black bg-opacity-70 text-white py-2 px-3 rounded-pill fw-bold">
                      {selectedDoc.url.toLowerCase().split('?')[0].endsWith('.pdf') ? '📄 Archivo PDF' : '📸 Imagen del Documento'}
                    </span>
                  </div>
                  
                  {selectedDoc.url.toLowerCase().split('?')[0].endsWith('.pdf') && (
                    <div className="position-absolute top-2 right-2 z-3">
                      <a 
                        href={getDocumentUrl(selectedDoc.url)} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="btn btn-primary btn-sm fw-bold d-flex align-items-center gap-1.5 shadow-sm"
                      >
                        <Eye size={14} /> Abrir PDF
                      </a>
                    </div>
                  )}

                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {imageError ? (
                      <Alert variant="warning" className="text-center m-3 border-0">
                        <AlertTriangle size={24} className="text-warning mb-2" />
                        <h6 className="fw-bold text-dark">⚠️ Vista previa no disponible</h6>
                        <p className="small text-muted mb-3">Es posible que el archivo requiera descargarse directamente.</p>
                        <a 
                          href={getDocumentUrl(selectedDoc.url)} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn btn-outline-warning btn-sm fw-bold shadow-sm"
                        >
                          Abrir enlace del archivo
                        </a>
                      </Alert>
                    ) : selectedDoc.url.toLowerCase().split('?')[0].endsWith('.pdf') ? (
                      <iframe
                        src={getDocumentUrl(selectedDoc.url)}
                        title="Visor PDF"
                        style={{
                          width: '100%',
                          height: '90%',
                          border: 'none',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                        }}
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <img
                        src={getDocumentUrl(selectedDoc.url)}
                        alt="Documento cargado"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                        }}
                        onError={() => setImageError(true)}
                      />
                    )}
                  </div>
                </Col>

                {/* Lado Derecho: Controles, Vehículo e IA */}
                <Col lg={6} className="p-4 d-flex flex-column justify-content-between" style={{ height: '75vh', overflowY: 'auto' }}>
                  <div>
                    <h5 className="fw-bold text-dark mb-1">Auditoría del Documento</h5>
                    <p className="text-muted small border-bottom pb-2 mb-3">Verifica datos del chofer, vehículo y asigna N° de Móvil.</p>

                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <User size={16} className="text-primary" />
                        <span className="fw-bold text-dark fs-6">{selectedDoc.driver_name || 'Conductor'}</span>
                      </div>
                      <div className="text-muted small mb-2">RUT: {selectedDoc.driver_rut || 'S/I'} | Tel: {selectedDoc.driver_phone || 'S/I'}</div>
                      <Badge bg="secondary" className="text-uppercase tracking-wider py-1.5 px-2.5 mb-3">
                        {selectedDoc.document_type === 'licencia_frontal' ? '🪪 Licencia (Frente)' :
                         selectedDoc.document_type === 'licencia_reverso' ? '🪪 Licencia (Reverso)' :
                         selectedDoc.document_type === 'rut_frontal' ? '🇨🇱 Cédula (Frente)' :
                         selectedDoc.document_type === 'rut_reverso' ? '🇨🇱 Cédula (Reverso)' :
                         selectedDoc.document_type}
                      </Badge>
                    </div>

                    {/* TARJETA DE VEHÍCULO INGRESADO POR EL CHOFER */}
                    {selectedDoc.vehicle ? (
                      <Card className="bg-light border-0 rounded-3 mb-3">
                        <Card.Header className="bg-dark text-white fw-bold py-2 small d-flex align-items-center justify-content-between">
                          <span>🚘 Vehículo Registrado</span>
                          <span className="badge bg-primary font-monospace">{selectedDoc.vehicle.plate}</span>
                        </Card.Header>
                        <Card.Body className="p-3 small">
                          <Row className="g-2">
                            <Col xs={6}>
                              <span className="text-muted">Marca / Modelo:</span>
                              <div className="fw-bold text-dark">{selectedDoc.vehicle.make || 'S/I'} {selectedDoc.vehicle.model || ''}</div>
                            </Col>
                            <Col xs={6}>
                              <span className="text-muted">Año / Categoría:</span>
                              <div className="fw-bold text-dark">{selectedDoc.vehicle.year || 'S/I'} ({selectedDoc.vehicle.vehicle_type_name})</div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ) : (
                      <Alert variant="warning" className="p-2 small mb-3">
                        ⚠️ El conductor aún no ha completado la ficha de su vehículo en el onboarding.
                      </Alert>
                    )}

                    {/* ASIGNACIÓN DE N° DE MÓVIL Y PROVEEDOR (EXCLUSIVO LOCUTORA) */}
                    <Card className="border border-primary border-opacity-25 rounded-3 mb-3 bg-blue-50" style={{ backgroundColor: '#f0f7ff' }}>
                      <Card.Body className="p-3">
                        <h6 className="fw-bold text-primary mb-2 d-flex align-items-center gap-1.5">
                          <Car size={18} /> Asignación de Flota Interna
                        </h6>

                        {/* Campo N° Móvil */}
                        <Form.Group className="mb-2">
                          <Form.Label className="small fw-bold text-dark mb-1">N° de Móvil Asignado *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Ej: 104"
                            value={internalCode}
                            onChange={(e) => setInternalCode(e.target.value.toUpperCase())}
                            className="font-monospace fw-bold bg-white"
                            isInvalid={!mobileStatus.is_available}
                          />
                          {checkingMobile && <small className="text-muted">Verificando disponibilidad...</small>}
                        </Form.Group>

                        {/* Alerta de Conflicto y Sugerencias de Móvil */}
                        {!mobileStatus.is_available && mobileStatus.conflict && (
                          <Alert variant="danger" className="p-2 mb-2 small border-0 shadow-sm">
                            <div className="fw-bold d-flex align-items-center gap-1">
                              <ShieldAlert size={14} /> ¡N° Móvil {internalCode} Ocupado!
                            </div>
                            <div>Pertenece a la patente: <strong>{mobileStatus.conflict.plate}</strong> ({mobileStatus.conflict.driver_name}).</div>
                            
                            {mobileStatus.suggestions && mobileStatus.suggestions.length > 0 && (
                              <div className="mt-2 pt-2 border-top border-danger border-opacity-25">
                                <span className="text-dark font-semibold">Sugerencias libres:</span>
                                <div className="d-flex gap-1.5 mt-1">
                                  {mobileStatus.suggestions.map((sug) => (
                                    <Button
                                      key={sug}
                                      variant="outline-danger"
                                      size="sm"
                                      className="py-0 px-2 fw-bold font-monospace bg-white"
                                      onClick={() => setInternalCode(sug)}
                                    >
                                      Usar {sug}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </Alert>
                        )}

                        {/* Campo Proveedor / Empresa */}
                        <Form.Group>
                          <Form.Label className="small fw-bold text-dark mb-1">Proveedor / Flotero Asignado</Form.Label>
                          <Form.Select
                            value={providerId}
                            onChange={(e) => setProviderId(e.target.value)}
                            className="bg-white small"
                          >
                            <option value="">Conductor Directo / Independiente</option>
                            {providers.map((p) => (
                              <option key={p.id} value={p.id}>
                                🏢 {p.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Card.Body>
                    </Card>

                    {/* Fecha de Expiración Confirmada */}
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold text-dark small mb-1">Fecha Vencimiento Documento</Form.Label>
                      <Form.Control
                        type="date"
                        value={expirationDate}
                        onChange={(e) => setExpirationDate(e.target.value)}
                        disabled={submitting}
                        className="bg-white"
                      />
                    </Form.Group>

                    {/* Formulario de Rechazo */}
                    {showRejectionInput && (
                      <Form.Group className="mb-3 animate-fade-in">
                        <Form.Label className="fw-bold text-danger small mb-1">Motivo de Rechazo</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Ej: Foto borrosa, vencido, etc."
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          disabled={submitting}
                          required
                        />
                      </Form.Group>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="border-top pt-3 mt-2">
                    {showRejectionInput ? (
                      <div className="d-flex gap-2">
                        <Button 
                          variant="danger" 
                          className="flex-grow-1 fw-bold" 
                          onClick={() => handleReview('rejected')}
                          disabled={submitting}
                        >
                          {submitting ? 'Enviando...' : 'Confirmar Rechazo'}
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => setShowRejectionInput(false)}
                          disabled={submitting}
                        >
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          className="flex-grow-1 fw-bold d-flex align-items-center justify-content-center gap-1.5"
                          onClick={() => handleReview('approved')}
                          disabled={submitting || !mobileStatus.is_available}
                        >
                          <Check size={18} /> Aprobar Documento y Móvil
                        </Button>
                        <Button
                          variant="outline-danger"
                          className="fw-bold d-flex align-items-center justify-content-center gap-1.5"
                          onClick={() => setShowRejectionInput(true)}
                          disabled={submitting}
                        >
                          <X size={18} /> Rechazar
                        </Button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-muted py-5">
                <Eye size={48} className="mb-3 text-secondary" />
                <h5>Ningún documento seleccionado</h5>
                <p className="small text-muted">Selecciona un documento de la lista izquierda para auditarlo.</p>
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default AdminDocumentReview;
